import * as React from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ReactDOM from "react-dom/client";

import "bootstrap/dist/css/bootstrap.min.css";
import "./styles/index.css";

import { Auth, AuthContext } from "./context";
import AboutUs from "./pages/AboutUs";
import Home from "./pages/Home";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Logout from "./pages/Logout";
import OpenRsvp from "./pages/OpenRsvp";
import Photos from "./pages/Photos";
import QandA from "./pages/QandA";
import Reception from "./pages/Reception";
import Responses from "./pages/Responses";
import Rsvp from "./pages/Rsvp";
import SaveTheDate from "./pages/SaveTheDate";
import Schedule from "./pages/Schedule";
import Updates from "./pages/Updates";
import { registryUrl } from "./components/Navbar";

const ProtectedRoute = ({ children }: { children: React.ReactElement }) => {
  const { fetch } = React.useContext(AuthContext);
  const { search, pathname } = useLocation();

  if (!fetch) {
    const params = new URLSearchParams(search);
    params.append("redirect", pathname);
    return <Navigate replace to={"/login?" + params.toString()} />;
  }

  return children;
};

const App = () => {
  const createPartialAuthContext = (auth: Auth) => ({
    email: auth.email,
    fetch: (input: RequestInfo | URL, init?: RequestInit | undefined) => {
      const headers = new Headers({
        ...init?.headers,
        Authorization: "Basic " + btoa(`${auth.email}:${auth.accessToken}`),
      });

      return fetch(input, { ...init, headers });
    },
  });

  const [partialAuthContext, setPartialAuthContext] = React.useState<Required<
    Pick<React.ContextType<typeof AuthContext>, "fetch" | "email">
  > | null>(() => {
    const authJson = localStorage.getItem("auth-v0");
    if (authJson) {
      try {
        return createPartialAuthContext(JSON.parse(authJson) as Auth);
      } catch (error) {
        console.error("Failed to parse auth JSON", error);
        localStorage.removeItem("auth-v0");
      }
    }
    return null;
  });

  const setAuth = React.useCallback(
    (auth: Auth) => {
      localStorage.setItem("auth-v0", JSON.stringify(auth));
      setPartialAuthContext(() => createPartialAuthContext(auth));
    },
    [setPartialAuthContext]
  );

  const resetAuth = React.useCallback(() => {
    setPartialAuthContext(null);
    localStorage.removeItem("auth-v0");
  }, []);

  const RegistryRedirect = () => {
    window.location.replace(registryUrl);
    return null;
  };

  return (
    <HelmetProvider>
      <AuthContext.Provider value={{ setAuth, ...partialAuthContext }}>
        <BrowserRouter>
          <Routes>
            <Route element={<Home />} path="/">
              <Route path="*" />
            </Route>
            <Route element={<Layout />} path="/">
              <Route element={<Logout resetAuth={resetAuth} />} path="logout" />
              <Route element={<AboutUs />} path="about-us" />
              <Route element={<Updates />} path="updates" />
              <Route element={<OpenRsvp />} path="open-rsvp" />
              <Route element={<RegistryRedirect />} path="registry" />
              <Route element={<Photos />} path="photos/:accessToken" />
              <Route
                element={
                  <ProtectedRoute>
                    <Rsvp />
                  </ProtectedRoute>
                }
                path="rsvp/*"
              />
              <Route element={<QandA />} path="q-and-a/*" />
              <Route
                element={
                  <ProtectedRoute>
                    <Schedule />
                  </ProtectedRoute>
                }
                path="schedule"
              />
              <Route
                element={
                  <ProtectedRoute>
                    <Responses />
                  </ProtectedRoute>
                }
                path="responses"
              />
              <Route element={<Login />} path="login" />
              <Route element={<Reception />} path="reception" />
            </Route>
            {...["/savethedate", "/save-the-date", "/std"].map((p) => (
              <Route element={<SaveTheDate />} key={p} path={p} />
            ))}
          </Routes>
        </BrowserRouter>
      </AuthContext.Provider>
    </HelmetProvider>
  );
};

ReactDOM.createRoot(document.getElementById("root")!).render(<App />);
